import { processPrescriptionInfo } from './processPrescritionInfo'
// Constants
const SEPARATOR: string = ':'
const CHECK_MARK_UNICODE: string = '\u2713'

interface Transformer {
  to?: (key: string, value: any, options: any) => Record<string, any>
  from?: (transformedEntries: Array<[string, any]>) => Record<string, any>
}

interface Transformers {
  [key: string]: Transformer
}

interface FileId {
  attachmentId: string
  name: string
}

interface Image {
  name: string
  type: string
  id?: string
}

export function addIdsToImages(answers: any): any {
  const fileIdMap: Record<string, string> = answers.fileIds.reduce((acc: Record<string, string>, fileId: FileId) => {
    acc[fileId.name] = fileId.attachmentId
    return acc
  }, {})

  Object.keys(answers).forEach((key: string) => {
    if (key.startsWith('image.')) {
      answers[key] = answers[key].map((image: Image) => {
        if (fileIdMap[image.name]) {
          return { ...image, id: fileIdMap[image.name] }
        }
        return image
      })
    }
  })

  return answers
}

const TRANSFORMERS: Transformers = {
  objectOption: {
    to: (key: string, value: any, options: any): Record<string, any> => {
      const transformed: Record<string, any> = { [options.fullKey]: value }
      for (const [propKey, propValue] of Object.entries(value)) {
        transformed[`${key}_${propKey}`] = propValue
      }
      return transformed
    },
  },
  checkMark: {
    to: (key: string, value: any, options: any): Record<string, any> => {
      if (typeof value === 'string') {
        return { [`${key}${SEPARATOR}r${SEPARATOR}${value}`]: CHECK_MARK_UNICODE }
      } else if (Array.isArray(value)) {
        return value.reduce(
          (acc, option) => ({ ...acc, [`${key}${SEPARATOR}c${SEPARATOR}${option}`]: CHECK_MARK_UNICODE }),
          {},
        )
      } else if (typeof value === 'boolean') {
        return { [`${key}${SEPARATOR}s${SEPARATOR}${value ? 'y' : 'n'}`]: CHECK_MARK_UNICODE }
      }
      return {}
    },
  },
  s: {
    from: (entries) => {
      const result: any = {}
      entries.forEach(([key]) => {
        const [base, _, option] = key.split(SEPARATOR)
        result[`${base}:checkMark`] = option === 'y'
      })
      return result
    },
  },
  c: {
    from: (entries) => {
      const result: any = {}
      entries.forEach(([key]) => {
        const [base, _, option] = key.split(SEPARATOR)
        if (!result[`${base}:checkMark`]) result[`${base}:checkMark`] = []
        result[`${base}:checkMark`].push(option)
      })
      return result
    },
  },
  r: {
    from: (entries) => {
      const result: any = {}
      entries.forEach(([key]) => {
        const [base, _, option] = key.split(SEPARATOR)
        result[`${base}:checkMark`] = option
      })
      return result
    },
  },
}

// Define the type for the processing functions
type ProcessingFunction = (answers: Record<string, any>, direction: 'to' | 'from', options: any) => Record<string, any>

// Map survey types to their respective special processing functions
const specialProcessing: Record<string, ProcessingFunction> = {
  prescription: processPrescriptionInfo,
}

// currently we only do value transformation for dates, if needed refactor this to handle valye tyle and process it
function transformDate(value: any, direction: 'to' | 'from'): string {
  if (!value || typeof value !== 'string') {
    return value
  }
  if (direction === 'to' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // Convert from YYYY-MM-DD to DD/MM/YYYY
    const [year, month, day] = value.split('-')
    return `${day}/${month}/${year}`
  } else if (direction === 'from' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    // Convert from DD/MM/YYYY to YYYY-MM-DD
    const [day, month, year] = value.split('/')
    return `${year}-${month}-${day}`
  } else {
    // Return the original value if it doesn't match the expected date format
    return value
  }
}

/**
 * Processes survey answers, either transforming them to a custom format or reverting them back.
 * Additionally, applies special processing for certain survey types.
 * @param answers A record containing the survey answers.
 * @param direction The direction of processing: 'to' for transforming or 'from' for reverting.
 * @param surveyType The type of the survey, which may trigger special processing.
 * @returns A record of the processed survey answers.
 */
export function processSurveyAnswers({
  answers,
  direction,
  surveyType,
  isCompleted,
}: {
  answers: Record<string, any>
  direction: 'to' | 'from'
  surveyType?: string
  isCompleted?: boolean
}): Record<string, any> {
  if (surveyType && specialProcessing[surveyType]) {
    answers = specialProcessing[surveyType](answers, direction, { isCompleted })
  }

  if (direction === 'to') {
    const hasImageKey = Object.keys(answers).some((key) => key.startsWith('image.'))
    if (hasImageKey) {
      answers = addIdsToImages(answers)
    }
    return Object.entries(answers).reduce((acc: Record<string, any>, [key, value]) => {
      const parts = key.split(SEPARATOR)
      const prefix = parts[0]
      const transformerKey = parts.length > 1 ? parts[1] : null

      if (transformerKey && TRANSFORMERS[transformerKey]) {
        const transformedData = TRANSFORMERS[transformerKey].to!(prefix, value, { fullKey: key, isCompleted })
        return { ...acc, ...transformedData }
      } else {
        acc[key] = transformDate(value, direction)
        return acc
      }
    }, {})
  } else {
    let result: any = {}
    Object.entries(answers).forEach(([key, value]) => {
      const parts = key.split(SEPARATOR)
      const type = parts[1]
      if (TRANSFORMERS[type]?.from && typeof TRANSFORMERS[type]?.from === 'function') {
        const transformer = TRANSFORMERS[type].from as unknown as any
        const transformedData = transformer([[key, value]])
        Object.keys(transformedData).forEach((k) => {
          const data = transformedData[k]
          if (Array.isArray(result[k])) {
            result[k] = [...new Set([...result[k], ...data])] // Ensure uniqueness for array types
          } else {
            result[k] = data
          }
        })
      } else {
        result[key] = transformDate(value, direction)
      }
    })

    return result
  }
}
